/**
 * The media picture (M4 Part B): the per-locale variant grid and the
 * cluster-level matrix.
 *
 * The API hands back the raw read — every locale's manifest plus every stored
 * object — because the arithmetic is presentation. Q17's matrix exists because
 * the per-locale panel is right for editing and structurally cannot show that
 * one screenshot is missing its dark variants in eleven languages, which is
 * exactly the gap that ships broken.
 */

import { FILE_SIZE_LIMITS } from "@atsready/shared/constants";
import type { BlogMediaObject, BlogMediaState } from "./types";

export const MEDIA_THEMES = ["light", "dark"] as const;
export const MEDIA_VIEWPORTS = ["desktop", "mobile"] as const;

export type MediaTheme = (typeof MEDIA_THEMES)[number];
export type MediaViewport = (typeof MEDIA_VIEWPORTS)[number];

/** Four slots per screenshot per locale: light/dark × desktop/mobile. */
export const VARIANTS_PER_SCREENSHOT = MEDIA_THEMES.length * MEDIA_VIEWPORTS.length;

export const BLOG_MEDIA_MAX_BYTES = FILE_SIZE_LIMITS.blogMediaImageBytes;
export const BLOG_MEDIA_CONTENT_TYPE = "image/png";

export interface VariantSlot {
  theme: MediaTheme;
  viewport: MediaViewport;
  /** `null` when this slot has no bytes anywhere — rendered as a defect. */
  object: BlogMediaObject | null;
}

/** Every slot for one screenshot in one locale, in a stable reading order. */
export function variantSlotsFor(
  objects: readonly BlogMediaObject[],
  args: { locale: string; screenshotId: string },
): VariantSlot[] {
  const slots: VariantSlot[] = [];
  for (const theme of MEDIA_THEMES) {
    for (const viewport of MEDIA_VIEWPORTS) {
      const object =
        objects.find(
          (candidate) =>
            candidate.locale === args.locale &&
            candidate.screenshotId === args.screenshotId &&
            candidate.theme === theme &&
            candidate.viewport === viewport,
        ) ?? null;
      slots.push({ theme, viewport, object });
    }
  }
  return slots;
}

export interface MediaMatrix {
  screenshots: string[];
  locales: string[];
  /** `${screenshotId}.${locale}` → how many of the four variants exist. */
  cells: Record<string, number>;
}

/**
 * Fold the raw read into the matrix.
 *
 * `requiredScreenshots` comes from the body the caller already loaded
 * (`requiredVariantsFor`). Without it a screenshot with *zero* variants in
 * every language would simply not appear as a row — which is the one gap the
 * matrix exists to make visible.
 */
export function deriveMediaMatrix(
  state: BlogMediaState,
  requiredScreenshots: readonly string[] = [],
): MediaMatrix {
  const screenshots = [
    ...new Set([...requiredScreenshots, ...state.objects.map((o) => o.screenshotId)]),
  ].sort();
  const locales = [
    ...new Set([...state.heads.map((head) => head.locale), ...state.objects.map((o) => o.locale)]),
  ].sort();

  const cells: Record<string, number> = {};
  for (const screenshotId of screenshots) {
    for (const locale of locales) {
      cells[`${screenshotId}.${locale}`] = state.objects.filter(
        (object) => object.screenshotId === screenshotId && object.locale === locale,
      ).length;
    }
  }
  return { screenshots, locales, cells };
}

export function cellCount(matrix: MediaMatrix, screenshotId: string, locale: string): number {
  return matrix.cells[`${screenshotId}.${locale}`] ?? 0;
}

/**
 * Q20: an incomplete cell is a defect, not a warning.
 *
 * There is no rendering fallback for a missing variant — the marketing suite
 * already fails on one — so the same fact appears here and in the publish
 * verdict, styled the same way in both.
 */
export function describeCell(count: number): {
  label: string;
  tone: "complete" | "defect";
  srLabel: string;
} {
  const complete = count >= VARIANTS_PER_SCREENSHOT;
  return {
    label: `${String(count)}/${String(VARIANTS_PER_SCREENSHOT)}`,
    tone: complete ? "complete" : "defect",
    srLabel: complete
      ? `All ${String(VARIANTS_PER_SCREENSHOT)} variants present`
      : `${String(VARIANTS_PER_SCREENSHOT - count)} of ${String(VARIANTS_PER_SCREENSHOT)} variants missing`,
  };
}

/**
 * Where a variant's bytes are actually served from.
 *
 * Committed variants resolve through a repo-relative convention that means
 * nothing on the admin origin, so admin prefixes the marketing host — the
 * `resolveFigureSrc` override the renderer takes for exactly this reason.
 */
export function mediaVariantUrl(
  baseUrl: string,
  args: { slug: string; object: BlogMediaObject },
): string {
  const { object } = args;
  if (object.storage === "r2") {
    // Already content-addressed: the hash IS the name, so a changed variant is
    // a different URL and a cached copy can never be the wrong bytes.
    return `${baseUrl}/blog-media/${object.hash}.png`;
  }
  // Committed variants live at a stable filename served with `max-age=86400`,
  // so a browser holds a copy for a day — including the response HEADERS it
  // arrived with. That is not a theoretical problem: correcting the marketing
  // Cross-Origin-Resource-Policy fixed the origin immediately, and this panel
  // kept showing broken cells for every variant already in the cache, while
  // the ones that happened not to be cached loaded fine.
  //
  // The hash makes the URL name the bytes, the way the R2 path already does.
  // It changes when a variant is re-shot and not otherwise, so the day-long
  // cache still does its job — it just can no longer be wrong.
  const path = `${baseUrl}/images/blog/${args.slug}/${object.screenshotId}.${object.locale}.${object.theme}.${object.viewport}.png`;
  return `${path}?v=${object.hash}`;
}

/** Q25: committed files are pinned by hash too, so both chips carry one. */
export function provenanceChip(object: BlogMediaObject): { label: string; hash: string } {
  return {
    label: object.storage === "r2" ? "uploaded" : "committed",
    hash: object.hash.slice(0, 8),
  };
}

export type UploadRejection = "not_png" | "too_large" | "wrong_aspect";

export interface UploadPrecheck {
  ok: boolean;
  rejection: UploadRejection | null;
  message: string;
  /** `wrong_aspect` is overridable; the others are not. */
  overridable: boolean;
}

const OK_PRECHECK: UploadPrecheck = { ok: true, rejection: null, message: "", overridable: false };

/**
 * The client-side pre-flight.
 *
 * Deliberately advisory: publish re-runs every one of these server-side (Q27),
 * so a "use it anyway" here cannot smuggle a broken capture past the gate — it
 * only saves the operator a round trip on the obvious cases.
 */
export function precheckUpload(args: {
  type: string;
  size: number;
  viewport: MediaViewport;
  dimensions?: { width: number; height: number } | undefined;
}): UploadPrecheck {
  if (args.type !== BLOG_MEDIA_CONTENT_TYPE) {
    return {
      ok: false,
      rejection: "not_png",
      message: "Screenshots must be PNG. Every capture in the corpus is one.",
      overridable: false,
    };
  }
  if (args.size > BLOG_MEDIA_MAX_BYTES) {
    return {
      ok: false,
      rejection: "too_large",
      message: `That file is larger than the ${String(
        Math.round(BLOG_MEDIA_MAX_BYTES / (1024 * 1024)),
      )} MB ceiling.`,
      overridable: false,
    };
  }
  if (args.dimensions !== undefined && !aspectMatchesViewport(args.dimensions, args.viewport)) {
    return {
      ok: false,
      rejection: "wrong_aspect",
      message: `A ${args.viewport} slot expects a ${
        args.viewport === "desktop" ? "landscape" : "portrait"
      } capture. This one is ${String(args.dimensions.width)}×${String(args.dimensions.height)}.`,
      overridable: true,
    };
  }
  return OK_PRECHECK;
}

const PNG_MAGIC = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

/**
 * Width and height straight out of the PNG header.
 *
 * Read from the bytes rather than by loading an `Image`: the aspect warning has
 * to be available synchronously once the file is read, and an `Image` load is
 * an extra async hop that fails silently in every environment without a real
 * decoder. IHDR is always the first chunk — 8-byte signature, 4-byte length,
 * 4-byte type, then two big-endian uint32s — which is the same read the server
 * does, so the two cannot disagree about a file.
 */
export function pngDimensions(bytes: Uint8Array): { width: number; height: number } | null {
  if (bytes.length < 24) return null;
  if (!PNG_MAGIC.every((byte, index) => bytes[index] === byte)) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return { width: view.getUint32(16), height: view.getUint32(20) };
}

/** Desktop captures are landscape, mobile portrait. A swap is the common error. */
export function aspectMatchesViewport(
  dimensions: { width: number; height: number },
  viewport: MediaViewport,
): boolean {
  const landscape = dimensions.width > dimensions.height;
  return viewport === "desktop" ? landscape : !landscape;
}

export function slotLabel(theme: MediaTheme, viewport: MediaViewport): string {
  return `${theme} · ${viewport}`;
}
